import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore.js';
import BudgetResponseToast from './components/BudgetResponseToast.jsx';

// Eager: auth screens are the critical initial path
import Welcome     from './pages/Welcome.jsx';
import Login       from './pages/Login.jsx';
import Register    from './pages/Register.jsx';
import VerifyPhone from './pages/VerifyPhone.jsx';
import VerifyEmail from './pages/VerifyEmail.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';

// Lazy: role-specific bundles
const ClientDashboard        = lazy(() => import('./pages/client/ClientDashboard.jsx'));
const Appointments           = lazy(() => import('./pages/client/Appointments.jsx'));
const ServiceHistory         = lazy(() => import('./pages/client/ServiceHistory.jsx'));
const MechanicDashboard      = lazy(() => import('./pages/mechanic/MechanicDashboard.jsx'));
const ScanQR                 = lazy(() => import('./pages/mechanic/ScanQR.jsx'));
const ScanQRWithInspection   = lazy(() => import('./pages/mechanic/ScanQRWithInspection.jsx'));
const VehicleDetail          = lazy(() => import('./pages/mechanic/VehicleDetail.jsx'));
const NewBudget              = lazy(() => import('./pages/mechanic/NewBudget.jsx'));
const MyBudgets              = lazy(() => import('./pages/mechanic/MyBudgets.jsx'));
const WorkOrder              = lazy(() => import('./pages/mechanic/WorkOrder.jsx'));
const BillingDashboard       = lazy(() => import('./pages/billing/BillingDashboard.jsx'));
const AdminDashboard         = lazy(() => import('./pages/admin/AdminDashboard.jsx'));
const EmployeeLogs           = lazy(() => import('./pages/admin/EmployeeLogs.jsx'));
const FraudDashboard         = lazy(() => import('./pages/admin/FraudDashboard.jsx'));
const ServicesStatus         = lazy(() => import('./pages/admin/ServicesStatus.jsx'));
const FeatureFlags           = lazy(() => import('./pages/admin/FeatureFlags.jsx'));
const ReceptionIntake        = lazy(() => import('./pages/reception/ReceptionIntake.jsx'));
const SurveyRespond          = lazy(() => import('./pages/SurveyRespond.jsx'));

function HomeRedirect() {
  const { user } = useAuthStore();
  if (!user) return <Welcome />;
  const routes = {
    client:      '/client',
    mechanic:    '/mechanic',
    electrician: '/mechanic',
    painter:     '/mechanic',
    reception:   '/reception',
    billing:     '/billing',
    admin:       '/admin',
  };
  return <Navigate to={routes[user.role] || '/login'} replace />;
}

function Guard({ roles, children }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Loading() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', color:'var(--silver-light,#cbd5e1)', background:'var(--bg1,#0d1829)' }}>
      Cargando…
    </div>
  );
}

const STAFF  = ['mechanic', 'electrician', 'painter', 'reception', 'admin'];
const BILLING = ['billing', 'admin'];

export default function App() {
  return (
    <BrowserRouter>
      <BudgetResponseToast />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/"              element={<HomeRedirect />} />
          <Route path="/login"         element={<Login />} />
          <Route path="/register"      element={<Register />} />
          <Route path="/verify-phone"  element={<VerifyPhone />} />
          <Route path="/verify-email"  element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Client */}
          <Route path="/client"              element={<Guard roles={['client']}><ClientDashboard /></Guard>} />
          <Route path="/client/appointments" element={<Guard roles={['client']}><Appointments /></Guard>} />
          <Route path="/client/history"      element={<Guard roles={['client']}><ServiceHistory /></Guard>} />
          <Route path="/survey/:token"       element={<SurveyRespond />} />

          {/* Mechanic / Electrician / Painter */}
          <Route path="/mechanic"             element={<Guard roles={STAFF}><MechanicDashboard /></Guard>} />
          <Route path="/mechanic/scan"        element={<Guard roles={STAFF}><ScanQRWithInspection /></Guard>} />
          <Route path="/mechanic/vehicle/:id" element={<Guard roles={STAFF}><VehicleDetail /></Guard>} />
          <Route path="/mechanic/budget/:id"  element={<Guard roles={STAFF}><NewBudget /></Guard>} />
          <Route path="/mechanic/budgets"     element={<Guard roles={STAFF}><MyBudgets /></Guard>} />
          <Route path="/mechanic/order/:id"   element={<Guard roles={STAFF}><WorkOrder /></Guard>} />

          {/* QR scan deep link — any staff */}
          <Route path="/scan/:vehicleId"      element={<Guard roles={STAFF}><ScanQR /></Guard>} />

          {/* Reception */}
          <Route path="/reception" element={<Guard roles={['reception','admin']}><ReceptionIntake /></Guard>} />

          {/* Billing */}
          <Route path="/billing" element={<Guard roles={BILLING}><BillingDashboard /></Guard>} />

          {/* Admin */}
          <Route path="/admin"              element={<Guard roles={['admin']}><AdminDashboard /></Guard>} />
          <Route path="/admin/employee/:id" element={<Guard roles={['admin']}><EmployeeLogs /></Guard>} />
          <Route path="/admin/fraud"        element={<Guard roles={['admin']}><FraudDashboard /></Guard>} />
          <Route path="/admin/services"     element={<Guard roles={['admin']}><ServicesStatus /></Guard>} />
          <Route path="/admin/flags"        element={<Guard roles={['admin']}><FeatureFlags /></Guard>} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
